import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import './style.css'

// Views
import CalendarView from './views/CalendarView.vue'
import LoginView from './views/LoginView.vue'
import InviteView from './views/InviteView.vue'
import RequestView from './views/RequestView.vue'
import AdminView from './views/AdminView.vue'
import HouseView from './views/HouseView.vue'
import GuideView from './views/GuideView.vue'
import CompletionView from './views/CompletionView.vue'
import ConductView from './views/ConductView.vue'
import PrivacyView from './views/PrivacyView.vue'
import DataDeletionView from './views/DataDeletionView.vue'
import InventoryView from './views/InventoryView.vue'
import InventoryItemView from './views/InventoryItemView.vue'
import ConsumablesView from './views/ConsumablesView.vue'
import ChecklistView from './views/ChecklistView.vue'

// Stores
import { useAuthStore } from './stores/auth'

// Router - Conditions (HouseView) is now the default landing page
const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: HouseView, meta: { requiresAuth: true } },
    { path: '/calendar', component: CalendarView, meta: { requiresAuth: true } },
    { path: '/login', component: LoginView },
    { path: '/invite', component: InviteView, meta: { requiresAuth: true } },
    { path: '/request', component: RequestView, meta: { requiresAuth: true } },
    { path: '/admin', component: AdminView, meta: { requiresAuth: true, requiresOwner: true } },
    { path: '/conduct', component: ConductView, meta: { requiresAuth: true } },
    { path: '/guide', component: GuideView, meta: { requiresAuth: true } },
    { path: '/completion', component: CompletionView, meta: { requiresAuth: true } },
    { path: '/inventory', component: InventoryView, meta: { requiresAuth: true } },
    { path: '/inventory/:id', component: InventoryItemView, meta: { requiresAuth: true } },
    { path: '/consumables', component: ConsumablesView, meta: { requiresAuth: true } },
    { path: '/checklists', component: ChecklistView, meta: { requiresAuth: true } },
    { path: '/checklists/:id', component: ChecklistView, meta: { requiresAuth: true } },
    // Public pages (no auth required) - needed for Facebook app review
    { path: '/privacy', component: PrivacyView },
    { path: '/data-deletion', component: DataDeletionView },
  ],
})

// Auth guard
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('session_token') || new URLSearchParams(window.location.search).get('token')

  // Save token from URL if present
  if (window.location.search.includes('token=')) {
    const urlToken = new URLSearchParams(window.location.search).get('token')
    if (urlToken) {
      localStorage.setItem('session_token', urlToken)
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }

  if (to.meta.requiresAuth && !token) {
    next('/login')
    return
  }

  // Check invite code and Code of Conduct (except for public pages)
  if (to.meta.requiresAuth && to.path !== '/login' && to.path !== '/invite') {
    const authStore = useAuthStore()
    // Wait for auth check if still loading
    if (authStore.loading) {
      await authStore.checkAuth()
    }
    // Guests must enter invite code before accessing any page
    if (authStore.needsInvite) {
      next('/invite')
      return
    }
    // Redirect to CoC page if not agreed (except conduct page itself)
    if (to.path !== '/conduct' && authStore.user && !authStore.hasAgreedToCoc) {
      next('/conduct')
      return
    }
  }

  next()
})

// Create app
const app = createApp(App)
app.use(createPinia())
app.use(router)
app.mount('#app')
